const API_URL = process.env.NEXT_PUBLIC_API_URL;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  name?: string;
  last_name?: string;
  telephone?: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  last_name: string;
  telephone: string;
  address: string;
  locality: string;
  image: string | null;
  is_active: boolean;
}

export interface AuthTokens {
  token: string;
  "refresh-token": string;
  user: User;
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

async function apiCall<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (!API_URL) {
    const err: ApiError = {
      message: "Configuration error: NEXT_PUBLIC_API_URL is not set",
    };
    throw err;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    let errors: Record<string, string[]> | undefined;
    try {
      errors = await res.json();
    } catch {
      // ignore parse errors
    }
    const err: ApiError = { message: `HTTP ${res.status}`, errors };
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export async function loginApi(credentials: LoginCredentials): Promise<AuthTokens> {
  return apiCall<AuthTokens>("/login/", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
}

export async function registerApi(data: RegisterData): Promise<User> {
  return apiCall<User>("/users/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getUserApi(id: number, token: string): Promise<User> {
  return apiCall<User>(`/users/${id}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}


export async function updateUserApi(
  id: number,
  data: Partial<Omit<User, "id" | "is_active">>,
  token: string,
): Promise<User> {
  return apiCall<User>(`/users/${id}/`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
}

export async function refreshTokenApi(refresh: string): Promise<{ access: string; refresh: string }> {
  return apiCall<{ access: string; refresh: string }>("/token/refresh/", {
    method: "POST",
    body: JSON.stringify({ refresh }),
  });
}

export async function logoutApi(token: string): Promise<void> {
  return apiCall<void>("/logout/", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function requestPasswordResetApi(email: string): Promise<void> {
  return apiCall<void>("/users/password_request_reset/", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export interface ReferralSummary {
  name: string;
  email: string;
  contract: boolean;
}

export interface CampaignUserSummary {
  campaign_title: string;
  campaign_url: string;
  user_url: string;
  country: string;
  start_date: string;
  end_date: string;
  recommended_count: number;
  accepted_count: number;
  completed_count: number;
}

export interface UserDashboard {
  link: string;
  recommended_count: number;
  accepted_count: number;
  completed_count: number;
  referrals: ReferralSummary[];
}

export async function getCampaignsApi(userId: number, token: string): Promise<CampaignUserSummary[]> {
  const result = await apiCall<CampaignUserSummary | CampaignUserSummary[]>(
    `/campaigns/${userId}/my-campaigns/`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return Array.isArray(result) ? result : [result];
}

export async function getCampaignDashboardApi(
  campaignUrl: string,
  identifier: string,
  token: string,
): Promise<UserDashboard> {
  return apiCall<UserDashboard>(`/campaigns/${campaignUrl}/t/${identifier}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function decodeJwt(token: string): { user_id?: number; id?: number; sub?: number | string; exp?: number } | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    let base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4 !== 0) {
      base64 += "=";
    }
    const decoded = JSON.parse(atob(base64));
    return decoded;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const decoded = decodeJwt(token);
  // If the token can't be decoded or has no exp claim, assume it is still valid
  // and let the API return 401 when it actually expires.
  if (!decoded?.exp) return false;
  return decoded.exp * 1000 < Date.now() + 30_000; // 30s buffer
}
